import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Shield, Heart, ArrowLeft, Loader } from 'lucide-react';
import { SiteFooter } from '../components/SiteFooter';
import { useLegalContent } from '../utils/legalContent';

type TabType = 'terms' | 'privacy' | 'ethics';

function isTabType(t: string | null): t is TabType {
  return t === 'terms' || t === 'privacy' || t === 'ethics';
}

export const AppTOSPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const t =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('tab') : null;
    return isTabType(t) ? t : 'terms';
  });
  const { content, loading, error } = useLegalContent();

  useEffect(() => {
    const t = searchParams.get('tab');
    if (isTabType(t)) setActiveTab(t);
  }, [searchParams]);


  const tabs = [
    {
      id: 'terms' as TabType,
      label: 'Terms of Service',
      icon: FileText,
      color: 'text-brand'
    },
    {
      id: 'privacy' as TabType,
      label: 'Privacy Policy',
      icon: Shield,
      color: 'text-accent'
    },
    {
      id: 'ethics' as TabType,
      label: 'AI Ethics & Values',
      icon: Heart,
      color: 'text-status-warning'
    }
  ];

  const renderTermsContent = () => {
    if (!content) return null;
    const terms = content.termsOfService;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-th-primary mb-2">
            {terms.title}
          </h2>
          <p className="text-sm sm:text-base text-th-primary">
            Last updated: {content.lastUpdated}
          </p>
        </div>

        {/* Terms Content */}
        <div className="space-y-6">
          {terms.sections.map((section, index) => (
            <div
              key={index}
              className="card p-6"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-th-primary mb-4">
                {section.title}
              </h3>
              <p className="text-sm sm:text-base text-th-primary leading-relaxed mb-4">
                {section.content}
              </p>
              {section.list && (
                <ul className="list-disc list-inside text-sm sm:text-base text-th-primary space-y-2 ml-4">
                  {section.list.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <div className="card p-6">
          <h3 className="text-lg sm:text-xl font-semibold text-th-primary mb-4">
            {terms.contact.title}
          </h3>
          <p className="text-sm sm:text-base text-th-primary mb-3">
            {terms.contact.content}
          </p>
          <div className="text-sm sm:text-base text-th-primary">
            <p>Email: {terms.contact.email}</p>
            {terms.contact.website && (
              <p>Website: {terms.contact.website}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPrivacyContent = () => {
    if (!content) return null;
    const privacy = content.privacyPolicy;

    const iconMap: Record<string, any> = {
      'Eye': FileText,
      'Lock': Shield,
      'Database': FileText,
      'Shield': Shield
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-th-primary mb-2">
            {privacy.title}
          </h2>
          <p className="text-sm sm:text-base text-th-primary">
            {privacy.subtitle}
          </p>
        </div>

        {/* Privacy Principles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {privacy.principles.map((principle, index) => {
            const IconComponent = iconMap[principle.icon] || Shield;
            return (
              <div key={index} className="card p-4">
                <IconComponent className={`w-8 h-8 ${principle.color} mb-3`} />
                <h4 className="text-sm sm:text-base text-th-primary font-semibold mb-2">{principle.title}</h4>
                <p className="text-xs sm:text-sm text-th-primary">
                  {principle.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Privacy Content */}
        <div className="space-y-6">
          {privacy.sections.map((section, index) => (
            <div key={index} className="card p-6">
              <h3 className="text-lg sm:text-xl font-semibold text-th-primary mb-4">
                {section.title}
              </h3>
              
              {section.subsections ? (
                <div className="space-y-4">
                  {section.subsections.map((subsection, subIndex) => (
                    <div key={subIndex}>
                      <h4 className="text-sm sm:text-base text-th-primary font-medium mb-2">{subsection.title}</h4>
                      <p className="text-xs sm:text-sm text-th-primary">{subsection.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <p className="text-sm sm:text-base text-th-primary leading-relaxed mb-4">
                    {section.content}
                  </p>
                  {section.list && (
                    <ul className="list-disc list-inside text-sm sm:text-base text-th-primary space-y-2 ml-4">
                      {section.list.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.security && (
                    <div className="bg-surface p-4 rounded-lg mt-4">
                      <h4 className="text-sm sm:text-base text-th-primary font-medium mb-2">{section.security.title}</h4>
                      <ul className="text-xs sm:text-sm text-th-primary space-y-1">
                        {section.security.measures.map((measure, measureIndex) => (
                          <li key={measureIndex}>• {measure}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <div className="card p-6">
          <h3 className="text-lg sm:text-xl font-semibold text-th-primary mb-4">
            {privacy.contact.title}
          </h3>
          <p className="text-sm sm:text-base text-th-primary mb-3">
            {privacy.contact.content}
          </p>
          <div className="text-sm sm:text-base text-th-primary">
            <p>Email: {privacy.contact.email}</p>
            {privacy.contact.website && (
              <p>Website: {privacy.contact.website}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEthicsContent = () => {
    if (!content) return null;
    const ethics = content.aiEthics;

    const iconMap: Record<string, any> = {
      'Heart': Heart,
      'Shield': Shield,
      'Users': FileText,
      'Brain': FileText
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-th-primary mb-2">
            {ethics.title}
          </h2>
          <p className="text-sm sm:text-base text-th-primary">
            {ethics.subtitle}
          </p>
        </div>

        {/* Core Principles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {ethics.principles.map((principle, index) => {
            const IconComponent = iconMap[principle.icon] || Heart;
            return (
              <div key={index} className="card p-6">
                <IconComponent className={`w-8 h-8 ${principle.color} mb-4`} />
                <h4 className="text-base sm:text-lg font-semibold text-th-primary mb-2">
                  {principle.title}
                </h4>
                <p className="text-sm sm:text-base text-th-primary">
                  {principle.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Detailed Ethics Policy */}
        <div className="space-y-6">
          {ethics.sections.map((section, index) => (
            <div key={index} className="card p-6">
              <h3 className="text-lg sm:text-xl font-semibold text-th-primary mb-4">
                {section.title}
              </h3>
              <p className="text-sm sm:text-base text-th-primary leading-relaxed mb-4">
                {section.content}
              </p>
              {section.list && (
                <ul className="list-disc list-inside text-sm sm:text-base text-th-primary space-y-2 ml-4">
                  {section.list.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Commitment Statement */}
        <div className="card p-6 bg-brand/10 border border-brand/20">
          <h3 className="text-lg sm:text-xl font-semibold text-th-primary mb-4">
            {ethics.promise.title}
          </h3>
          <p className="text-sm sm:text-base text-th-primary leading-relaxed">
            {ethics.promise.content}
          </p>
        </div>

        {/* Contact Information */}
        <div className="card p-6">
          <h3 className="text-lg sm:text-xl font-semibold text-th-primary mb-4">
            {ethics.contact.title}
          </h3>
          <p className="text-sm sm:text-base text-th-primary mb-3">
            {ethics.contact.content}
          </p>
          <div className="text-sm sm:text-base text-th-primary">
            <p>Email: {ethics.contact.email}</p>
            {ethics.contact.committee && (
              <p>Ethics Committee: {ethics.contact.committee}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'terms':
        return renderTermsContent();
      case 'privacy':
        return renderPrivacyContent();
      case 'ethics':
        return renderEthicsContent();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
          <p className="text-th-primary">Loading legal content...</p>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-elevated border border-subtle rounded-lg p-8 max-w-md w-full text-center">
          <div className="text-status-error text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-th-primary mb-2">
            Content Load Error
          </h2>
          <p className="text-th-primary mb-4">
            Failed to load legal content. Please check your configuration.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="button-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-th-primary">
      {/* Header */}
      <header
        className="border-b border-subtle sticky top-0 z-30"
        style={{ backgroundColor: 'rgba(20, 20, 22, 0.95)' }}
      >
        <div className="px-4 py-4">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            {/* Logo/Brand */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
                <span className="text-white text-sm">⚡</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-th-primary">Hyper ProMax</h1>
                <p className="text-xs text-th-primary">Legal Information</p>
              </div>
            </div>

            {/* Back Button */}
            <button
              onClick={() => window.history.back()}
              className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-th-primary transition-colors rounded-lg hover:bg-overlay"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 pt-4 sm:pt-8">
        {/* Tab Navigation */}
        <div className="mb-4 sm:mb-8">
          <div className="card p-1 sm:p-2">
            <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchParams({ tab: tab.id }, { replace: true });
                  }}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 sm:py-3 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-brand text-surface'
                      : 'text-th-primary hover:bg-overlay'
                  }`}
                >
                  <tab.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="h-[calc(100vh-200px)] overflow-y-auto">
          <div className="space-y-6">
            {renderContent()}
          </div>
        </div>
      </div>
      <SiteFooter />

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};